/**
 * The revision's field changes, restated as things an academic reader can act on.
 *
 * @module components/portfolio/comparison/academic-changes
 */

import { humanizeIdentifier } from "@/components/shared/diff-labels";
import type { RevisionFieldChange } from "@/server/governance-diff";
import type { Programme } from "@/types";

import type { AcademicChange, MappingDifference } from "./diff-types";
import { equivalentWeightingChange } from "./module-differences";

function finalPathField(path: string): string {
  const finalSegment = path.split(".").at(-1) ?? path;
  return finalSegment.includes("[")
    ? (finalSegment.split("[", 1)[0] ?? finalSegment)
    : finalSegment;
}

function pathId(path: string, collection: string): string | undefined {
  return new RegExp(`${collection}\\[([^\\]]+)]`).exec(path)?.[1];
}

function describeValue(value: unknown, field: string): string {
  if (value === undefined || value === null || value === "") {
    return "not set";
  }
  if (typeof value === "boolean") {
    return value ? "yes" : "no";
  }
  if (typeof value === "string" || typeof value === "number") {
    return `${value}${field === "weighting" || field === "weight" ? "%" : ""}`;
  }
  if (Array.isArray(value)) {
    return `${value.length} ${value.length === 1 ? "item" : "items"}`;
  }
  return "structured content";
}

function withoutTerminalPunctuation(value: string): string {
  return value.replace(/[.!?]+$/, "");
}

function mimloLabels(programme: Programme, moduleId: string | undefined, value: unknown): unknown {
  if (!moduleId || !Array.isArray(value)) {
    return value;
  }
  const module = (programme.modules ?? []).find((entry) => entry.id === moduleId);
  const labels = new Map((module?.mimlos ?? []).map((mimlo) => [mimlo.id, mimlo.text]));
  return value.map((id) => (typeof id === "string" ? (labels.get(id) ?? "Unknown outcome") : id));
}

function resolveAcademicItem(
  before: Programme,
  after: Programme,
  path: string,
): { item: string; itemContext?: string; itemType: string } {
  const moduleId = pathId(path, "modules");
  const beforeModule = (before.modules ?? []).find((module) => module.id === moduleId);
  const afterModule = (after.modules ?? []).find((module) => module.id === moduleId);
  const module = afterModule ?? beforeModule;
  const moduleContext = module
    ? [module.code, module.title].filter(Boolean).join(" · ")
    : undefined;

  const assessmentId = pathId(path, "assessments");
  if (assessmentId) {
    const assessment =
      (afterModule?.assessments ?? []).find((entry) => entry.id === assessmentId) ??
      (beforeModule?.assessments ?? []).find((entry) => entry.id === assessmentId);
    return {
      item: assessment?.title ?? assessment?.type ?? "Untitled assessment",
      ...(moduleContext ? { itemContext: moduleContext } : {}),
      itemType: "Assessment",
    };
  }

  const mimloId = pathId(path, "mimlos");
  if (mimloId) {
    const mimlo =
      (afterModule?.mimlos ?? []).find((entry) => entry.id === mimloId) ??
      (beforeModule?.mimlos ?? []).find((entry) => entry.id === mimloId);
    return {
      item: mimlo?.text ?? "Untitled module learning outcome",
      ...(moduleContext ? { itemContext: moduleContext } : {}),
      itemType: "Module learning outcome",
    };
  }

  if (moduleId) {
    return {
      item: module?.title ?? "Untitled module",
      ...(module?.code ? { itemContext: module.code } : {}),
      itemType: "Module",
    };
  }

  const ploId = pathId(path, "plos");
  if (ploId) {
    const plo =
      (after.plos ?? []).find((entry) => entry.id === ploId) ??
      (before.plos ?? []).find((entry) => entry.id === ploId);
    return {
      item: plo?.text ?? "Untitled programme learning outcome",
      ...(plo?.code ? { itemContext: plo.code } : {}),
      itemType: "Programme learning outcome",
    };
  }

  return {
    item: after.title || before.title || "Programme",
    itemType: "Programme",
  };
}

function academicDescription(
  change: RevisionFieldChange,
  field: string,
  item: { item: string; itemType: string },
): string {
  const itemName = withoutTerminalPunctuation(item.item);
  if (change.kind === "added" && change.after && typeof change.after === "object") {
    return `${item.itemType} added: ${itemName}.`;
  }
  if (change.kind === "removed" && change.before && typeof change.before === "object") {
    return `${item.itemType} removed: ${itemName}.`;
  }
  const before = withoutTerminalPunctuation(describeValue(change.before, field));
  const after = withoutTerminalPunctuation(describeValue(change.after, field));
  if (field === "weight" || field === "weighting") {
    return `${itemName} weighting changed from ${before} to ${after}.`;
  }
  if (field === "credits") {
    return `${itemName} credits changed from ${before} to ${after}.`;
  }
  if (field === "stage") {
    return `${itemName} moved from Stage ${before} to Stage ${after}.`;
  }
  if (field === "mimloIds") {
    return `Mapped module learning outcomes for ${itemName} changed from ${before} to ${after}.`;
  }
  if (field === "text" && item.itemType.includes("learning outcome")) {
    return `${item.itemType} text changed from “${before}” to “${after}”.`;
  }
  if (change.kind === "added") {
    return `${humanizeIdentifier(field)} added to ${itemName}: ${after}.`;
  }
  if (change.kind === "removed") {
    return `${humanizeIdentifier(field)} removed from ${itemName}: ${before}.`;
  }
  return `${humanizeIdentifier(field)} changed from ${before} to ${after}.`;
}

/** Project mapping edge differences into reviewer-facing relationship changes. */
export function academicMappingChanges(mappings: MappingDifference[]): AcademicChange[] {
  return mappings.map((mapping) => {
    const source = withoutTerminalPunctuation(mapping.sourceLabel);
    const target = withoutTerminalPunctuation(mapping.targetLabel);
    const relationship = `${source} → ${target}`;
    const context = [mapping.relationship, mapping.context ?? mapping.sourceContext]
      .filter(Boolean)
      .join(" · ");
    return {
      id: `mapping:${mapping.id}:${mapping.status}`,
      path: mapping.id,
      category: "mappings",
      kind: mapping.status,
      item: mapping.sourceLabel,
      ...(context ? { itemContext: context } : {}),
      field: mapping.relationship,
      description: `Mapping ${mapping.status}: ${relationship}.`,
      ...(mapping.status === "removed" ? { before: mapping.targetLabel } : {}),
      ...(mapping.status === "added" ? { after: mapping.targetLabel } : {}),
    };
  });
}

/** Project structural revision differences into reviewer-facing academic language. */
export function academicChanges(
  before: Programme,
  after: Programme,
  changes: RevisionFieldChange[],
): AcademicChange[] {
  return changes
    .filter((change) => !equivalentWeightingChange(change, changes))
    .map((change) => {
      const field = finalPathField(change.path);
      const item = resolveAcademicItem(before, after, change.path);
      const moduleId = pathId(change.path, "modules");
      const beforeValue =
        field === "mimloIds" ? mimloLabels(before, moduleId, change.before) : change.before;
      const afterValue =
        field === "mimloIds" ? mimloLabels(after, moduleId, change.after) : change.after;
      return {
        id: `${change.path}:${change.kind}`,
        path: change.path,
        category: change.category,
        kind: change.kind,
        item: item.item,
        ...(item.itemContext ? { itemContext: item.itemContext } : {}),
        field: humanizeIdentifier(field),
        description: academicDescription(change, field, item),
        ...(beforeValue === undefined ? {} : { before: beforeValue }),
        ...(afterValue === undefined ? {} : { after: afterValue }),
      };
    });
}
